import React from 'react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import BoardMeetingTenderRecap from './BoardMeetingTenderRecap';
import {
  Briefcase, TrendingUp, TrendingDown, BarChart3, Award, ArrowDownRight,
  ArrowUpRight, Truck, Users, Star, Calendar, X, ChevronRight, Sparkles,
} from 'lucide-react';

const MONTHS_FR = ['', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun'];

/**
 * MidYearBoardMeeting — strategic board "point de mi-année" (Jun 30).
 *
 * Mirrors `AnnualBoardMeeting`'s layout (header, P&L cards, monthly bars,
 * best/worst, KPIs) but covers H1 only and is **purely informational** :
 *   • no resolutions to vote on,
 *   • no treasury impact,
 *   • single "Continue" CTA to dismiss.
 *
 * Reads `pendingMidYearReview` from the store and dismisses via
 * `dismissMidYearReview`. Triggered automatically by `nextDay` at M6 D30.
 *
 * 31/05/2026 — narrative companion to the annual board.
 */
export default function MidYearBoardMeeting() {
  const { pendingMidYearReview, dismissMidYearReview, gameStarted, company } = useGameStore();
  if (!gameStarted || !pendingMidYearReview) return null;

  const r = pendingMidYearReview;
  const isProfit = r.netResult >= 0;
  const treasuryUp = r.treasuryDelta >= 0;

  // Build a 6-bar series so missing months render at 0.
  const monthsMap = new Map(r.months.map(m => [m.month, m]));
  const monthsAll = Array.from({ length: 6 }, (_, i) => {
    const monthNum = i + 1;
    return monthsMap.get(monthNum) || { month: monthNum, netResult: 0, totalRevenue: 0, totalExpenses: 0 };
  });
  const maxAbs = Math.max(...monthsAll.map(m => Math.abs(m.netResult || 0)), 1);

  // Chairman / president message — tuned for a MID-YEAR checkpoint
  // (lighter, more forward-looking than the annual address).
  const chairmanLine = (() => {
    if (r.marginPct >= 20) return 'Excellent premier semestre. La direction confirme la stratégie et prépare un S2 ambitieux.';
    if (r.marginPct >= 10) return 'Semestre solide. Quelques ajustements à étudier pour pousser la marge au S2.';
    if (r.marginPct >= 0)  return 'Équilibre tenu. Le conseil souhaite un focus sur la rentabilité contrat par contrat au S2.';
    if (r.marginPct >= -10) return 'Premier semestre tendu. Un plan d\'action S2 doit être proposé au prochain comité.';
    return 'Pertes significatives sur le S1. Le conseil exige un point hebdomadaire jusqu\'au redressement.';
  })();

  const handleClose = () => dismissMidYearReview();

  return (
    <div
      className="fixed inset-0 z-[88] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="midyear-board-meeting"
    >
      <div className="bg-ink-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[90vh] flex flex-col">
        {/* Header — same board-room leather feel, but amber accent for mid-year */}
        <div className="bg-gradient-to-r from-[#4d3a14] via-[#6b521e] to-[#4d3a14] px-6 py-5 flex items-center justify-between border-b border-amber-900/40 relative overflow-hidden">
          {[0, 1, 2].map(i => (
            <Sparkles
              key={`spark-${i}`}
              className="absolute text-amber-200/25 w-3 h-3"
              style={{
                top: `${20 + (i * 25) % 60}%`,
                left: `${10 + (i * 30) % 75}%`,
              }}
            />
          ))}
          <div className="flex items-center gap-3 text-white relative z-10">
            <div className="p-2 rounded-lg bg-white/10">
              <Briefcase className="w-5 h-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-amber-200/80 font-mono">
                Conseil d&apos;administration · Point semestriel
              </p>
              <h3 className="font-bold text-xl">Revue de mi-année {r.year}</h3>
              <p className="text-xs text-amber-100/70">{company?.name || 'HST'} — Clôture S1</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/60 hover:text-white transition-colors p-1 relative z-10"
            data-testid="close-midyear-report"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-6 space-y-5 overflow-y-auto">
          {/* Chairman's address */}
          <div className="bg-amber-900/15 border-l-4 border-amber-500 rounded-r-lg px-4 py-3">
            <p className="text-[10px] uppercase tracking-wider text-amber-300 font-mono">Mot du Président — point d&apos;étape</p>
            <p className="text-sm text-slate-200 italic mt-1" data-testid="midyear-chairman-address">« {chairmanLine} »</p>
          </div>

          {/* Top P&L cards */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-money/10 border border-money/20 rounded-xl p-3 text-center">
              <TrendingUp className="w-5 h-5 text-money mx-auto mb-1" />
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">CA S1</p>
              <p className="text-lg font-bold text-money font-mono" data-testid="midyear-revenue">{formatMoney(r.totalRevenue)}</p>
            </div>
            <div className="bg-danger/10 border border-danger/20 rounded-xl p-3 text-center">
              <TrendingDown className="w-5 h-5 text-danger mx-auto mb-1" />
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Charges S1</p>
              <p className="text-lg font-bold text-danger font-mono" data-testid="midyear-expenses">{formatMoney(r.totalExpenses)}</p>
            </div>
            <div className={`${isProfit ? 'bg-money/15 border-money/30' : 'bg-danger/15 border-danger/30'} border rounded-xl p-3 text-center`}>
              <BarChart3 className={`w-5 h-5 mx-auto mb-1 ${isProfit ? 'text-money' : 'text-danger'}`} />
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Résultat S1</p>
              <p className={`text-lg font-bold font-mono ${isProfit ? 'text-money' : 'text-danger'}`} data-testid="midyear-netresult">
                {isProfit ? '+' : ''}{formatMoney(r.netResult)}
              </p>
              <p className="text-[10px] text-slate-500 mt-0.5">Marge {r.marginPct >= 0 ? '+' : ''}{r.marginPct}%</p>
            </div>
          </div>

          {/* 6-month bar chart */}
          <div className="bg-ink-700/30 rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-mono mb-3">Évolution mensuelle (Résultat net)</p>
            <div className="grid grid-cols-6 gap-1 items-end h-28" data-testid="midyear-monthly-chart">
              {monthsAll.map(m => {
                const h = Math.round((Math.abs(m.netResult || 0) / maxAbs) * 100);
                const positive = (m.netResult || 0) >= 0;
                return (
                  <div key={m.month} className="flex flex-col items-center justify-end gap-1 h-full">
                    <div
                      className={`w-full rounded-sm transition-all ${positive ? 'bg-money/80' : 'bg-danger/80'}`}
                      style={{ height: `${Math.max(2, h)}%` }}
                      title={`${MONTHS_FR[m.month]} : ${(m.netResult || 0) >= 0 ? '+' : ''}${(m.netResult || 0).toLocaleString('fr-FR')} €`}
                      data-testid={`midyear-month-bar-${m.month}`}
                    />
                    <span className="text-[9px] text-slate-500 font-mono">{MONTHS_FR[m.month]}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Best / Worst months of H1 */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-money/5 border border-money/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-money" />
                <p className="text-[10px] uppercase tracking-wider text-money font-mono">Meilleur mois S1</p>
              </div>
              <p className="text-sm font-bold text-slate-200">{MONTHS_FR[r.bestMonth.month]} {r.year}</p>
              <p className="text-xs text-money font-mono">+{formatMoney(r.bestMonth.netResult)}</p>
            </div>
            <div className="bg-danger/5 border border-danger/20 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-4 h-4 text-danger" />
                <p className="text-[10px] uppercase tracking-wider text-danger font-mono">Mois le plus dur S1</p>
              </div>
              <p className="text-sm font-bold text-slate-200">{MONTHS_FR[r.worstMonth.month]} {r.year}</p>
              <p className="text-xs text-danger font-mono">{r.worstMonth.netResult >= 0 ? '+' : ''}{formatMoney(r.worstMonth.netResult)}</p>
            </div>
          </div>

          {/* Treasury delta + KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              {treasuryUp
                ? <ArrowUpRight className="w-4 h-4 text-money mx-auto mb-0.5" />
                : <ArrowDownRight className="w-4 h-4 text-danger mx-auto mb-0.5" />}
              <p className="text-[10px] text-slate-500">Variation trésorerie S1</p>
              <p className={`text-xs font-bold font-mono ${treasuryUp ? 'text-money' : 'text-danger'}`} data-testid="midyear-treasury-delta">
                {treasuryUp ? '+' : ''}{formatMoney(r.treasuryDelta)}
              </p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Truck className="w-4 h-4 text-slate-300 mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Véhicules</p>
              <p className="text-xs font-bold text-slate-200">{r.vehiclesTotal}</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Users className="w-4 h-4 text-slate-300 mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Chauffeurs</p>
              <p className="text-xs font-bold text-slate-200">{r.driversTotal}</p>
            </div>
            <div className="bg-ink-700/30 rounded-lg p-2.5 text-center">
              <Star className="w-4 h-4 text-warn mx-auto mb-0.5" />
              <p className="text-[10px] text-slate-500">Réputation / Notoriété</p>
              <p className="text-xs font-bold text-slate-200">{r.reputation} / {r.notoriety}</p>
            </div>
          </div>

          {/* Runway forward look — mid-year specific */}
          <div className="flex items-center justify-between bg-amber-900/10 border border-amber-700/30 rounded-lg px-3 py-2.5 text-xs">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-amber-400" />
              <span className="text-slate-300">Trésorerie de clôture S1</span>
            </div>
            <span className="font-bold font-mono text-slate-200">{formatMoney(r.treasuryEnd)}</span>
            <span className="text-slate-500 mx-2">·</span>
            <span className="text-slate-300">Runway projeté</span>
            <span className={`font-bold font-mono ${r.runwayMonths >= 6 ? 'text-money' : r.runwayMonths >= 3 ? 'text-warn' : 'text-danger'}`} data-testid="midyear-runway">
              {r.runwayMonths.toFixed(1)} mois
            </span>
          </div>

          {/* H1 highlights (acquisitions / departures) */}
          {(r.s1Highlights?.acquisitions > 0 || r.s1Highlights?.departures > 0) && (
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-ink-700/30 rounded-lg p-2.5 flex items-center gap-2" data-testid="midyear-acquisitions">
                <Truck className="w-4 h-4 text-money" />
                <span className="text-slate-300">{r.s1Highlights.acquisitions} véhicule{r.s1Highlights.acquisitions > 1 ? 's' : ''} acquis sur le S1</span>
              </div>
              <div className="bg-ink-700/30 rounded-lg p-2.5 flex items-center gap-2" data-testid="midyear-departures">
                <Users className="w-4 h-4 text-danger" />
                <span className="text-slate-300">{r.s1Highlights.departures} départ{r.s1Highlights.departures > 1 ? 's' : ''} chauffeur sur le S1</span>
              </div>
            </div>
          )}

          {/* 02/06/2026 — Appels d'Offres recap (S1) */}
          <BoardMeetingTenderRecap stats={r.tenderStats} scope="S1" />

          {/* Continue CTA */}
          <button
            onClick={handleClose}
            className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 rounded-xl text-sm font-bold transition-colors active:scale-[0.98] flex items-center justify-center gap-2"
            data-testid="midyear-continue-btn"
          >
            Poursuivre l&apos;exercice {r.year}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
